"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { authService } from "@/lib/auth-service";
import { getAdminToken } from "@/lib/api-client";
import Modal from "@/components/Modal";
import { logUsage } from "@/lib/logger";

// Configuration
const MAX_SESSIONS = 2; // Max concurrent sessions

export default function AuthManager({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const pathname = usePathname();
    const queryClient = useQueryClient();
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [logoutReason, setLogoutReason] = useState("");

    useEffect(() => {
        const checkSession = async () => {
            // Skip auth checks for public routes
            const publicRoutes = [
                '/',
                '/login',
                '/register',
                '/signup',
                '/forgot-password',
                '/reset-password',
                '/verify-email',
                '/unverified',
                '/terms',
                '/privacy',
                '/pricing',
                '/blog',
                '/contact',
                '/demo',
                '/presentation',
                '/admin',
                '/docs',
                '/landing-classic'
            ];
            // Normalize path for matching (remove trailing slash)
            const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
            const isPublicRoute = publicRoutes.some(route => {
                if (route === '/') return normalizedPath === '/';
                return normalizedPath === route || normalizedPath.startsWith(route + '/');
            });

            if (isPublicRoute) {
                return;
            }

            // Standalone HUD routes manage their own authentication & share-token flow
            if (normalizedPath === '/summary-standalone' || normalizedPath === '/whale-standalone') {
                return;
            }

            // Launcher & standalone routes handle share keys (#key= / ?key=)
            const isShareKeyRoute =
                normalizedPath === '/dashboard/summary' ||
                normalizedPath === '/dashboard/whale' ||
                normalizedPath === '/vol-oi-monitor';
            if (isShareKeyRoute && typeof window !== 'undefined' && (window.location.hash.includes('key=') || window.location.search.includes('key='))) {
                return;
            }

            // Check if user is authenticated by verifying actual session from server
            try {
                const user = await authService.getCurrentUser();

                // If no valid session, redirect to login
                if (!user) {
                    const isAdminAuth = typeof window !== 'undefined' && !!getAdminToken();
                    if (!isAdminAuth) {
                        console.log('[AuthManager] No valid session found, redirecting to login');
                        handleLogout("Your session has expired. Please sign in again.");
                        return;
                    }
                }

                // Session is valid, log page view
                logUsage(`Page View: ${pathname}`, { path: pathname });
            } catch (error) {
                console.warn('[AuthManager] Session check encountered network error (preserving session):', error);
                // Don't logout on network errors, only on actual auth failures
            }
        };

        // Run checks on mount and route change
        checkSession();

        // Set up interval for periodic checks (every 30 mins instead of 5)
        const interval = setInterval(checkSession, 30 * 60 * 1000);
        return () => clearInterval(interval);
    }, [pathname]);

    const handleLogout = (reason: string) => {
        setLogoutReason(reason);
        setShowLogoutModal(true);
        // Clear React Query cache to ensure UI reflects logged-out state immediately
        queryClient.removeQueries({ queryKey: ['auth'] });
        authService.logout();
    };

    const onModalClose = () => {
        setShowLogoutModal(false);
        router.push("/login");
    };

    return (
        <>
            {children}
            <Modal
                isOpen={showLogoutModal}
                onClose={onModalClose}
                title="Session Ended"
            >
                <p className="text-lg">{logoutReason}</p>
                <p className="mt-2 text-gray-400">You will be redirected to the login page.</p>
            </Modal>
        </>
    );
}
